import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, addWeeks, format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { getIndustrialCostProductionReportWeeklyActivity } from '../services/industrialCostService';
import type {
  ProductionLineConfig,
  ProductionReportWeeklyActivityBlock,
  ProductionReportWeeklyActivityWindow,
} from '../types/industrialCost';

type Props = { configuredLines: ProductionLineConfig[] };
type Kind = ProductionReportWeeklyActivityBlock['event_type'];
type DisplayMode = 'groups' | 'lines';
type LineRow = {
  id: string;
  label: string;
  groupKey: string;
  groupLabel: string;
  displayOrder: number;
};
type GroupRow = { key: string; label: string; lines: LineRow[] };
type SelectedBlock = {
  block: ProductionReportWeeklyActivityBlock;
  startMinute: number;
  endMinute: number;
  lineLabel: string;
  shift: string;
  reportStatus: string;
};

const LABEL_WIDTH = 174;
const WEEK_HOURS = 7 * 24;
const WEEK_MINUTES = WEEK_HOURS * 60;
const UNASSIGNED_GROUP = 'Sin grupo asignado';

const STYLE: Record<Kind, { color: string; label: string; text: string }> = {
  production: { color: '#22c55e', label: 'Producción', text: '#052e16' },
  setup: { color: '#fbbf24', label: 'Preparación', text: '#422006' },
  stop: { color: '#ef4444', label: 'Parada', text: '#fff' },
  idle: { color: '#38bdf8', label: 'Sin orden planificada', text: '#082f49' },
  unaccounted: { color: '#64748b', label: 'Sin registrar', text: '#fff' },
  pending: { color: '#334155', label: 'Pendiente (tiempo futuro)', text: '#cbd5e1' },
};

const isoDate = (value: Date) => format(value, 'yyyy-MM-dd');
const isoLocalDateTime = () => format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");

/** The backend sends local, timezone-naive production times. Use wall-clock minutes
 * for an axis with exactly 24 visual hours per day, including DST-change weeks. */
function wallMinute(value: string): number {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return Number.NaN;
  return Date.UTC(
    Number(match[1]), Number(match[2]) - 1, Number(match[3]),
    Number(match[4]), Number(match[5]),
  ) / 60000;
}

function wallMinuteForDate(value: string): number {
  return wallMinute(`${value}T00:00:00`);
}

function formatWallMinute(value: number): string {
  const date = new Date(value * 60000);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  const hour = String(date.getUTCHours()).padStart(2, '0');
  const minute = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

function clippedMinutes(
  startAt: string,
  endAt: string,
  weekStartMinute: number,
): { start: number; end: number; duration: number } | null {
  const rawStart = wallMinute(startAt);
  const rawEnd = wallMinute(endAt);
  if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return null;
  const start = Math.max(0, rawStart - weekStartMinute);
  const end = Math.min(WEEK_MINUTES, rawEnd - weekStartMinute);
  if (end <= start) return null;
  return { start, end, duration: end - start };
}

function groupKey(label: string): string {
  return label.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function lineIdForWindow(window: ProductionReportWeeklyActivityWindow): string {
  return window.production_line_config_id || `text:${window.line_or_center}`;
}

function formatMinutes(value: number): string {
  const rounded = Math.round(value);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  return hours ? `${hours} h${minutes ? ` ${minutes} min` : ''}` : `${minutes} min`;
}

function TimelineTrack({
  line,
  windows,
  weekStartMinute,
  pixelsPerHour,
  onOpen,
}: {
  line: LineRow;
  windows: ProductionReportWeeklyActivityWindow[];
  weekStartMinute: number;
  pixelsPerHour: number;
  onOpen: (selection: SelectedBlock) => void;
}) {
  const width = WEEK_HOURS * pixelsPerHour;
  const x = (minutes: number) => minutes / 60 * pixelsPerHour;
  const visibleWindows = windows.map(window => ({
    window,
    clip: clippedMinutes(window.start_at, window.end_at, weekStartMinute),
  })).filter((item): item is { window: ProductionReportWeeklyActivityWindow; clip: NonNullable<typeof item.clip> } => Boolean(item.clip));

  return (
    <Box sx={{ display: 'flex', minHeight: 80, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
      <Box sx={{
        width: LABEL_WIDTH, flex: `0 0 ${LABEL_WIDTH}px`, position: 'sticky', left: 0, zIndex: 4,
        bgcolor: '#0f172a', display: 'flex', alignItems: 'center', px: 1.5,
        borderRight: '1px solid rgba(148,163,184,0.2)',
      }}>
        <Typography noWrap title={line.label} sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '0.82rem' }}>
          {line.label}
        </Typography>
      </Box>
      <Box sx={{ width, flex: `0 0 ${width}px`, height: 80, position: 'relative', bgcolor: '#0b1220' }}>
        {Array.from({ length: 7 }, (_, day) => (
          <Box key={day} sx={{
            position: 'absolute', left: day * 24 * pixelsPerHour, top: 0, bottom: 0,
            width: 24 * pixelsPerHour, bgcolor: day % 2 ? 'rgba(148,163,184,0.035)' : 'transparent',
          }} />
        ))}
        {visibleWindows.map(({ window, clip }) => (
          <Tooltip key={window.report_id} arrow title={`${window.shift} · ${formatWallMinute(weekStartMinute + clip.start)} — ${formatWallMinute(weekStartMinute + clip.end)} · ${window.report_status === 'draft' ? 'Abierto' : 'Cerrado'}`}>
            <Box sx={{
              position: 'absolute', left: x(clip.start), width: x(clip.duration), top: 12, bottom: 12,
              bgcolor: 'rgba(148,163,184,0.12)', borderTop: '1px solid rgba(148,163,184,0.32)',
              borderBottom: '1px solid rgba(148,163,184,0.32)',
            }} />
          </Tooltip>
        ))}
        {visibleWindows.flatMap(({ window }) => window.blocks.map((block, index) => {
          const clip = clippedMinutes(block.start_at, block.end_at, weekStartMinute);
          if (!clip) return null;
          const style = STYLE[block.event_type];
          const title = block.event_type === 'production'
            ? (block.reference_label || 'Sin referencia')
            : block.event_type === 'setup' ? 'Prep.'
              : block.event_type === 'stop' ? 'Parada'
                : block.event_type === 'idle' ? 'Sin OF'
                  : block.event_type === 'unaccounted' ? 'Sin registrar' : 'Futuro';
          const tooltip = [
            `${style.label} · ${formatWallMinute(weekStartMinute + clip.start)} — ${formatWallMinute(weekStartMinute + clip.end)}`,
            `Duración visible: ${formatMinutes(clip.duration)}`,
            block.reference_label ? `Referencia: ${block.reference_label}` : '',
            block.manufacturing_order_number ? `OF: ${block.manufacturing_order_number}` : '',
            block.reason_label ? `Motivo: ${block.reason_label}` : '',
            `Turno: ${window.shift}`,
          ].filter(Boolean).join('\n');
          const select = () => onOpen({
            block, startMinute: weekStartMinute + clip.start, endMinute: weekStartMinute + clip.end,
            lineLabel: line.label, shift: window.shift, reportStatus: window.report_status,
          });
          return (
            <Tooltip key={`${window.report_id}-${block.event_id || block.event_type}-${index}`} arrow
              title={<Typography sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>{tooltip}</Typography>}>
              <Box role="button" tabIndex={0} aria-label={tooltip}
                onClick={select}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    select();
                  }
                }}
                sx={{
                  position: 'absolute', left: x(clip.start), width: x(clip.duration), top: 16, height: 48,
                  bgcolor: style.color, color: style.text, borderRadius: '3px',
                  display: 'flex', alignItems: 'center', overflow: 'hidden', px: 0.3,
                  cursor: 'pointer', borderRight: '1px solid rgba(2,6,23,0.4)',
                  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 1, zIndex: 5 },
                }}>
                {x(clip.duration) >= 35 && (
                  <Typography noWrap sx={{ color: 'inherit', fontSize: '0.72rem', fontWeight: 900, lineHeight: 1 }}>
                    {title}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        }))}
        {Array.from({ length: 8 }, (_, day) => (
          <Box key={day} sx={{
            position: 'absolute', pointerEvents: 'none', zIndex: 3,
            left: day * 24 * pixelsPerHour, top: 0, bottom: 0,
            borderLeft: '1px solid rgba(148,163,184,0.42)',
          }} />
        ))}
      </Box>
    </Box>
  );
}

export default function IndustrialCostWeeklyActivityView({ configuredLines }: Props) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [zoom, setZoom] = useState(0);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('groups');
  const [selectedGroupKey, setSelectedGroupKey] = useState('all');
  const [selectedLineId, setSelectedLineId] = useState('all');
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<string[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<SelectedBlock | null>(null);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const pixelsPerHour = 9 + zoom * 0.51;
  const markerStepHours = pixelsPerHour >= 45 ? 1 : pixelsPerHour >= 27 ? 2 : pixelsPerHour >= 16 ? 4 : 6;
  const weekEnd = addDays(weekStart, 6);
  const days = useMemo(() => Array.from({ length: 7 }, (_, day) => addDays(weekStart, day)), [weekStart]);
  const weekStartMinute = wallMinuteForDate(isoDate(weekStart));
  const width = WEEK_HOURS * pixelsPerHour;

  const query = useQuery({
    queryKey: ['industrial-cost-production-reports-weekly-activity', isoDate(weekStart)],
    queryFn: () => getIndustrialCostProductionReportWeeklyActivity({
      date_from: isoDate(weekStart), date_to: isoDate(weekEnd), as_of: isoLocalDateTime(),
    }),
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
  });
  const windows = query.data?.windows ?? [];

  const lineRows = useMemo<LineRow[]>(() => {
    const rows = configuredLines.map(line => {
      // Same grouping used by the current planning board; fall back to the line catalogue group.
      const groupLabel = line.process_group?.trim() || line.production_line_group_name?.trim() || UNASSIGNED_GROUP;
      return {
        id: line.id, label: line.name, groupKey: groupKey(groupLabel), groupLabel,
        displayOrder: line.display_order ?? 999999,
      };
    });
    const known = new Set(rows.map(line => line.id));
    for (const window of windows) {
      const id = lineIdForWindow(window);
      if (!known.has(id)) {
        rows.push({
          id, label: window.line_or_center, groupKey: groupKey(UNASSIGNED_GROUP),
          groupLabel: UNASSIGNED_GROUP, displayOrder: 999999,
        });
        known.add(id);
      }
    }
    return rows.sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, 'es'));
  }, [configuredLines, windows]);

  const windowsByLine = useMemo(() => {
    const byLine = new Map<string, ProductionReportWeeklyActivityWindow[]>();
    for (const window of windows) {
      const id = lineIdForWindow(window);
      byLine.set(id, [...(byLine.get(id) ?? []), window]);
    }
    return byLine;
  }, [windows]);

  const groups = useMemo<GroupRow[]>(() => {
    const byGroup = new Map<string, GroupRow>();
    for (const line of lineRows) {
      const group = byGroup.get(line.groupKey);
      if (group) group.lines.push(line);
      else byGroup.set(line.groupKey, { key: line.groupKey, label: line.groupLabel, lines: [line] });
    }
    return Array.from(byGroup.values());
  }, [lineRows]);

  const filteredGroups = selectedGroupKey === 'all' ? groups : groups.filter(group => group.key === selectedGroupKey);
  const filteredLines = filteredGroups.flatMap(group => group.lines)
    .filter(line => selectedLineId === 'all' || line.id === selectedLineId);
  const visibleLineIds = new Set(filteredLines.map(line => line.id));
  const visibleWindows = windows.filter(window => visibleLineIds.has(lineIdForWindow(window)));
  const available = visibleWindows.reduce((total, window) =>
    total + (clippedMinutes(window.start_at, window.end_at, weekStartMinute)?.duration ?? 0), 0);
  const accounted = visibleWindows.flatMap(window => window.blocks)
    .filter(block => !['pending', 'unaccounted'].includes(block.event_type))
    .reduce((total, block) => total + (clippedMinutes(block.start_at, block.end_at, weekStartMinute)?.duration ?? 0), 0);
  const pending = visibleWindows.flatMap(window => window.blocks)
    .filter(block => block.event_type === 'pending')
    .reduce((total, block) => total + (clippedMinutes(block.start_at, block.end_at, weekStartMinute)?.duration ?? 0), 0);
  const elapsedAvailable = Math.max(0, available - pending);

  const groupSummary = (group: GroupRow) => {
    const ids = new Set(group.lines
      .filter(line => selectedLineId === 'all' || line.id === selectedLineId)
      .map(line => line.id));
    const groupWindows = windows.filter(window => ids.has(lineIdForWindow(window)));
    const minutes = groupWindows.flatMap(window => window.blocks)
      .filter(block => !['pending', 'unaccounted'].includes(block.event_type))
      .reduce((sum, block) => sum + (clippedMinutes(block.start_at, block.end_at, weekStartMinute)?.duration ?? 0), 0);
    return `${group.lines.length} líneas · ${formatMinutes(minutes)} registrados`;
  };

  const renderLine = (line: LineRow) => (
    <TimelineTrack
      key={line.id}
      line={line}
      windows={windowsByLine.get(line.id) ?? []}
      weekStartMinute={weekStartMinute}
      pixelsPerHour={pixelsPerHour}
      onOpen={setSelectedBlock}
    />
  );

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{
        bgcolor: 'rgba(15,23,42,0.58)', border: '1px solid rgba(148,163,184,0.16)',
        borderRadius: 3, p: { xs: 1.75, md: 2.25 },
      }}>
        <Stack spacing={2}>
          <Stack spacing={0.25}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.1rem' }}>
              Actividad semanal
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
              Secuencia real de producción, preparaciones, paradas y tiempos sin orden por máquina.
            </Typography>
          </Stack>

          {/* Three independently readable controls: time range, scope and scale. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', lg: 'minmax(310px, 1.05fr) minmax(360px, 1.15fr) minmax(250px, 0.8fr)' }, gap: 1.5 }}>
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#0b1220', border: '1px solid rgba(148,163,184,0.16)', borderRadius: 2.5 }}>
              <Stack spacing={1}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 850, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Semana consultada
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontSize: '0.98rem', fontWeight: 900 }}>
                  {format(weekStart, 'dd/MM/yyyy')} – {format(weekEnd, 'dd/MM/yyyy')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  <Button size="small" variant="outlined" startIcon={<ChevronLeftRoundedIcon />}
                    onClick={() => setWeekStart(day => addWeeks(day, -1))}>Anterior</Button>
                  <Button size="small" variant="outlined" endIcon={<ChevronRightRoundedIcon />}
                    onClick={() => setWeekStart(day => addWeeks(day, 1))}>Siguiente</Button>
                  <Button size="small" variant="text" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                    Esta semana
                  </Button>
                  <Button size="small" variant="text" startIcon={query.isFetching ? <CircularProgress size={13} /> : <RefreshRoundedIcon />}
                    onClick={() => void query.refetch()} disabled={query.isFetching}>Actualizar</Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#0b1220', border: '1px solid rgba(148,163,184,0.16)', borderRadius: 2.5 }}>
              <Stack spacing={1.15}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 850, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Organización de las máquinas
                </Typography>
                <Stack direction="row" gap={0.75}>
                  <Button size="small" variant={displayMode === 'groups' ? 'contained' : 'outlined'}
                    onClick={() => setDisplayMode('groups')}>Por grupo</Button>
                  <Button size="small" variant={displayMode === 'lines' ? 'contained' : 'outlined'}
                    onClick={() => setDisplayMode('lines')}>Por línea</Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  <TextField select size="small" label="Grupo" value={selectedGroupKey}
                    onChange={event => { setSelectedGroupKey(event.target.value); setSelectedLineId('all'); }}
                    sx={{ minWidth: 0, flex: 1 }}>
                    <MenuItem value="all">Todos los grupos</MenuItem>
                    {groups.map(group => <MenuItem key={group.key} value={group.key}>{group.label}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Línea" value={selectedLineId}
                    onChange={event => setSelectedLineId(event.target.value)} sx={{ minWidth: 0, flex: 1 }}>
                    <MenuItem value="all">Todas las líneas</MenuItem>
                    {filteredGroups.flatMap(group => group.lines).map(line =>
                      <MenuItem key={line.id} value={line.id}>{line.label}</MenuItem>)}
                  </TextField>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#0b1220', border: '1px solid rgba(148,163,184,0.16)', borderRadius: 2.5 }}>
              <Stack spacing={0.9}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 850, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Escala temporal
                  </Typography>
                  <Typography sx={{ color: '#38bdf8', fontWeight: 900, fontSize: '0.78rem' }}>
                    {Math.round(pixelsPerHour)} px/h
                  </Typography>
                </Stack>
                <Slider size="small" min={0} max={100} step={1} value={zoom}
                  aria-label="Zoom de actividad semanal"
                  onChange={(_event, value) => setZoom(Array.isArray(value) ? value[0] : value)} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>Semana completa</Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>Detalle horario</Typography>
                </Stack>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                  Desplázate horizontalmente para recorrer los siete días.
                </Typography>
              </Stack>
            </Paper>
          </Box>

          <Box sx={{ borderTop: '1px solid rgba(148,163,184,0.14)', pt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Chip size="small" variant="outlined" label={`${formatMinutes(accounted)} registrados`}
              sx={{ color: '#86efac', borderColor: 'rgba(34,197,94,0.35)' }} />
            <Chip size="small" variant="outlined" label={`${formatMinutes(elapsedAvailable)} disponibles transcurridos`}
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.24)' }} />
            {pending > 0 && <Chip size="small" variant="outlined" label={`${formatMinutes(pending)} futuros`}
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.24)' }} />}
            <Typography sx={{ color: '#64748b', fontSize: '0.71rem' }}>
              Sin parte: disponibilidad desconocida.
            </Typography>
          </Box>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {(Object.keys(STYLE) as Kind[]).map(key => (
              <Stack key={key} direction="row" alignItems="center" gap={0.5}>
                <Box sx={{ height: 10, width: 10, borderRadius: 0.5, bgcolor: STYLE[key].color }} />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.71rem' }}>{STYLE[key].label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {query.isError && <Alert severity="error">No se pudo cargar la actividad semanal. Pulsa «Actualizar» para reintentar.</Alert>}
      {(query.data?.warnings ?? []).length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ bgcolor: 'rgba(120,53,15,0.12)', borderColor: 'rgba(251,191,36,0.3)' }}>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 850, color: '#fde68a' }}>
              {query.data!.warnings.length} {query.data!.warnings.length === 1 ? 'incidencia de registro' : 'incidencias de registro'} en esta semana
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#cbd5e1' }}>
              Los tramos señalados pueden estar omitidos o recortados; esto no significa que el parte completo sea inválido.
            </Typography>
            {(showAllWarnings ? query.data!.warnings : query.data!.warnings.slice(0, 2)).map((warning, index) => (
              <Box key={`${index}-${warning}`} sx={{ bgcolor: 'rgba(15,23,42,0.65)', borderRadius: 1.5, p: 1, borderLeft: '2px solid #fbbf24' }}>
                {warning.split('\n').map((line, part) => (
                  <Typography key={part} sx={{ fontSize: '0.78rem', color: part === 0 ? '#f8fafc' : '#cbd5e1', fontWeight: part === 0 ? 850 : 400 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            ))}
            {query.data!.warnings.length > 2 && (
              <Button size="small" variant="text" sx={{ alignSelf: 'flex-start', color: '#fbbf24' }}
                onClick={() => setShowAllWarnings(current => !current)}>
                {showAllWarnings ? 'Ver menos' : `Ver las ${query.data!.warnings.length - 2} incidencias restantes`}
              </Button>
            )}
          </Stack>
        </Alert>
      )}
      {query.isLoading ? <CircularProgress /> : (
        <Box sx={{ overflowX: 'auto', width: '100%', pb: 1, borderRadius: 2, border: '1px solid rgba(148,163,184,0.2)' }}>
          <Box sx={{ width: LABEL_WIDTH + width, minWidth: LABEL_WIDTH + width, bgcolor: '#0f172a' }}>
            <Box sx={{ display: 'flex', height: 72, borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
              <Box sx={{ width: LABEL_WIDTH, flex: `0 0 ${LABEL_WIDTH}px`, position: 'sticky', left: 0, zIndex: 5,
                bgcolor: '#0f172a', p: 1.5, borderRight: '1px solid rgba(148,163,184,0.2)' }}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>Máquina / línea</Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>Semana completa</Typography>
              </Box>
              <Box sx={{ position: 'relative', width, flex: `0 0 ${width}px`, height: 72 }}>
                {days.map((day, index) => (
                  <Box key={isoDate(day)} sx={{ position: 'absolute', left: index * 24 * pixelsPerHour,
                    width: 24 * pixelsPerHour, top: 0, bottom: 0, pl: 1, pt: 0.65,
                    bgcolor: index % 2 ? 'rgba(148,163,184,0.035)' : 'transparent',
                    borderLeft: '1px solid rgba(148,163,184,0.36)' }}>
                    <Typography noWrap sx={{ color: '#e2e8f0', textTransform: 'capitalize', fontSize: '0.8rem', fontWeight: 850 }}>
                      {format(day, 'EEEE dd/MM', { locale: es })}
                    </Typography>
                  </Box>
                ))}
                {days.flatMap((_day, dayIndex) => Array.from({ length: 24 / markerStepHours }, (_, tickIndex) => {
                  const hour = tickIndex * markerStepHours;
                  const left = (dayIndex * 24 + hour) * pixelsPerHour;
                  return (
                    <Box key={`${dayIndex}-${hour}`} sx={{ position: 'absolute', left, bottom: 5, height: 22,
                      borderLeft: '1px solid rgba(148,163,184,0.25)', pl: 0.4 }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.67rem' }}>{String(hour).padStart(2, '0')}:00</Typography>
                    </Box>
                  );
                }))}
              </Box>
            </Box>
            {displayMode === 'groups' ? filteredGroups.map(group => (
              <Fragment key={group.key}>
                <Box sx={{ display: 'flex', minHeight: 40, bgcolor: 'rgba(30,41,59,0.96)', borderBottom: '1px solid rgba(148,163,184,0.18)' }}>
                  <Box sx={{ width: LABEL_WIDTH, flex: `0 0 ${LABEL_WIDTH}px`, position: 'sticky', left: 0, zIndex: 4,
                    bgcolor: '#1e293b', display: 'flex', alignItems: 'center', px: 0.5,
                    borderRight: '1px solid rgba(148,163,184,0.2)' }}>
                    <Button size="small" onClick={() => setCollapsedGroupKeys(previous =>
                      previous.includes(group.key) ? previous.filter(key => key !== group.key) : [...previous, group.key]
                    )} startIcon={collapsedGroupKeys.includes(group.key) ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}
                      sx={{ color: '#e2e8f0', fontWeight: 900, justifyContent: 'flex-start', minWidth: 0, textTransform: 'none' }}>
                      {group.label}
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, color: '#94a3b8', fontSize: '0.73rem' }}>
                    {groupSummary(group)} · {collapsedGroupKeys.includes(group.key) ? 'Desplegar máquinas' : 'Ocultar máquinas'}
                  </Box>
                </Box>
                {!collapsedGroupKeys.includes(group.key) && group.lines
                  .filter(line => selectedLineId === 'all' || line.id === selectedLineId)
                  .map(renderLine)}
              </Fragment>
            )) : filteredLines.map(renderLine)}
          </Box>
          {filteredLines.length === 0 && <Alert severity="info">No hay líneas disponibles para mostrar.</Alert>}
        </Box>
      )}

      <Dialog open={Boolean(selectedBlock)} onClose={() => setSelectedBlock(null)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { bgcolor: '#0f172a', color: '#f8fafc' } }}>
        <DialogTitle>Detalle de actividad</DialogTitle>
        <DialogContent>
          {selectedBlock && (
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 850 }}>{STYLE[selectedBlock.block.event_type].label}</Typography>
              <Typography>Línea: {selectedBlock.lineLabel}</Typography>
              <Typography>Turno: {selectedBlock.shift}</Typography>
              <Typography>Inicio: {formatWallMinute(selectedBlock.startMinute)}</Typography>
              <Typography>Fin: {formatWallMinute(selectedBlock.endMinute)}</Typography>
              <Typography>Duración visible: {formatMinutes(selectedBlock.endMinute - selectedBlock.startMinute)}</Typography>
              {selectedBlock.block.reference_label && <Typography>Referencia: {selectedBlock.block.reference_label}</Typography>}
              {selectedBlock.block.manufacturing_order_number && <Typography>OF: {selectedBlock.block.manufacturing_order_number}</Typography>}
              {selectedBlock.block.reason_label && <Typography>Motivo: {selectedBlock.block.reason_label}</Typography>}
              <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>Estado del parte: {selectedBlock.reportStatus}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedBlock(null)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}
